import { useGetSubscriptionStatusQuery } from '../api';

/**
 * Custom hook to provide subscription-related data and derived values
 *
 * A user is considered a first-time user if:
 * - They have never purchased any credits (credits_total === 0)
 * - Note: New users now receive initial signup credits (default 20)
 *
 * @returns {Object} Object containing subscription data and derived values
 * @returns {boolean | undefined} isFirstTimeUser - true if first-time user, false otherwise, undefined if data not loaded
 * @returns {boolean | undefined} shouldShowOnboarding - true if onboarding should be shown, false otherwise, undefined if data not loaded
 * @returns {boolean | undefined} onboardingCompleted - true if onboarding is completed, false otherwise, undefined if data not loaded
 * @returns {SubscriptionStatusResponse | undefined} subscriptionStatus - raw subscription status data
 * @returns {boolean} isLoading - loading state of the subscription query
 * @returns {FetchBaseQueryError | SerializedError | undefined} error - any error from the subscription query
 */
export const useSubscription = () => {
	const {
		data: subscriptionStatus,
		isLoading,
		error,
		refetch,
	} = useGetSubscriptionStatusQuery();

	// First-time user = has initial signup credits but never purchased any
	const isFirstTimeUser =
		subscriptionStatus && subscriptionStatus.credits_total === 0;

	// Use backend onboarding data instead of localStorage
	const shouldShowOnboarding = subscriptionStatus?.should_show_onboarding;
	const onboardingCompleted = subscriptionStatus?.onboarding_completed;

	return {
		isFirstTimeUser,
		shouldShowOnboarding,
		onboardingCompleted,
		subscriptionStatus,
		isLoading,
		error,
		refetch,
	};
};
